/* In-page collection tabs (owner, 2026-09-26). The four tab groups and which
 * collections belong to them are defined in
 * sections/sondos-collection-header.liquid; this file is group-agnostic.
 *
 * The tabs are plain links, and every one of them is a real, server-rendered
 * page — a collection URL, sometimes with a view (`?view=tab-all`,
 * `?view=tab-shop`) so the header renders the right tab row on a reload.
 * Without this file a tap simply navigates there. This file only makes that
 * switch instant: she stays where she is, the product area below the tabs is
 * replaced, and the H1 / <title> follow the tab (data-sct-title).
 *
 * How the switch stays correct:
 *  - The section is fetched through the Section Rendering API for the tab's own
 *    URL, so the grid, the count, the filters and the auto-load sentinel
 *    (whose next-url keeps `view`) all belong to that category.
 *  - The address bar is moved to the tab's URL (replaceState — a tab is not a
 *    history step) BEFORE the grid is swapped. assets/sondos-collection.js keys
 *    its stored pages and its Back restore on the URL and re-arms when it sees
 *    a new #product-grid, so the auto-load, Back and reload all follow the tab
 *    and never mix «الكل» pages with a category's pages.
 *  - The swap goes through facets.js's own renderers — the exact path a sort or
 *    filter change already takes, which sondos-collection.js is built around.
 *  - The hidden `view` field in the two facet forms is kept in step, so a sort
 *    or filter inside a tab stays inside it (snippets/facets.liquid renders the
 *    same field on a reload).
 *
 * Short-lived, bounded HTML cache; intent and two adjacent tabs warmed only
 * on an unrestricted connection after load. No image preloads. A cached
 * response is parsed once and shared by the facet renderers.
 * While waiting the old grid is inert; delayed progress never resizes the
 * rail or grid. Only the latest interaction owns the result.
 */
(function () {
  'use strict';

  var rail = document.querySelector('[data-sct]');
  if (!rail || !window.fetch || !window.URL || !history.replaceState) return;

  var view = rail.getAttribute('data-sct-view') || '';
  var PENDING_MS = 160;
  var cache = {};
  var ticket = 0;
  var pendingTimer = 0;
  var pendingHref = '';
  var CACHE_MS = 5 * 60 * 1000;
  var CACHE_LIMIT = 6;
  var status = document.createElement('p');
  status.className = 'sct-status visually-hidden';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');
  rail.parentNode.appendChild(status);

  function keyOf(href) {
    var u = new URL(href, location.href);
    return u.pathname + '|' + (u.searchParams.get('view') || '');
  }

  var shown = keyOf(location.href);
  /* The description in the header was rendered for this tab only. */
  var origin = shown;

  function links() {
    return Array.prototype.slice.call(rail.querySelectorAll('a[data-sct-link]'));
  }

  function mark(link) {
    links().forEach(function (a) {
      if (a === link) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  }

  /* A chip half past the rail's edge is brought fully into the rail's view —
     the rail's own scrollLeft only, never the page. */
  function reveal(link) {
    var a = link.getBoundingClientRect();
    var r = rail.getBoundingClientRect();
    if (a.left < r.left) rail.scrollLeft -= (r.left - a.left) + 16;
    else if (a.right > r.right) rail.scrollLeft += (a.right - r.right) + 16;
  }

  function markByLocation() {
    var here = keyOf(location.href);
    var match = null;
    links().forEach(function (a) { if (keyOf(a.href) === here) match = a; });
    if (match) mark(match);
  }

  function collectionBox() {
    var c = document.getElementById('ProductGridContainer');
    return c && c.querySelector('.collection');
  }

  function sectionId() {
    var c = document.getElementById('ProductGridContainer');
    var s = c && c.closest('.shopify-section');
    return s && s.id ? s.id.replace(/^shopify-section-/, '') : '';
  }

  function fetchSection(href) {
    var key = keyOf(href);
    var old = cache[key];
    if (old && Date.now() - old.time < CACHE_MS) return old.promise;
    var id = sectionId();
    if (!id) return Promise.reject(new Error('no section'));
    var url = new URL(href, location.href);
    url.searchParams.set('section_id', id);
    var controller = window.AbortController ? new AbortController() : null;
    var entry = { time: Date.now(), controller: controller };
    var timeout;
    var request = fetch(url.href, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        if (html.indexOf('id="ProductGridContainer"') === -1) throw new Error('no grid');
        return html;
      });
    var p = Promise.race([request, new Promise(function (_, reject) {
      timeout = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('section timeout'));
      }, 12000);
    })]);
    entry.promise = p;
    cache[key] = entry;
    var keys = Object.keys(cache);
    while (keys.length > CACHE_LIMIT) {
      var first = keys.shift();
      if (cache[first].controller) cache[first].controller.abort();
      delete cache[first];
    }
    p.then(function () {
      clearTimeout(timeout);
      entry.controller = null;
    }, function () {
      clearTimeout(timeout);
      if (cache[key] === entry) delete cache[key];
    });
    return p;
  }

  function canWarm() {
    var connection = navigator.connection;
    return !document.hidden && !(window.Shopify && window.Shopify.designMode) &&
      !(connection && (connection.saveData || /(^|-)2g$|3g/.test(connection.effectiveType || '')));
  }

  function warm(link) {
    if (!link || !canWarm() || pendingHref || keyOf(link.href) === shown) return Promise.resolve();
    return fetchSection(link.href).catch(function () {});
  }

  /* One small pair per document, sequential and after the initial load. */
  function warmNeighbours() {
    setTimeout(function () {
      var work = function () {
        if (!canWarm() || pendingHref) return;
        var all = links();
        var index = all.findIndex(function (a) { return keyOf(a.href) === shown; });
        var next = all[index + 1];
        var other = all[index - 1] || all[index + 2];
        warm(next).then(function () { return warm(other); });
      };
      if (window.requestIdleCallback) window.requestIdleCallback(work, { timeout: 2000 });
      else work();
    }, 2000);
  }

  /* The hidden `view` field in the two submitted facet forms. */
  function syncViewField(target) {
    ['FacetFiltersForm', 'FacetFiltersFormMobile'].forEach(function (id) {
      var form = document.getElementById(id);
      if (!form) return;
      var field = form.querySelector('input[data-sct-view]');
      if (target) {
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'view';
          field.setAttribute('data-sct-view', '');
          form.insertBefore(field, form.firstChild);
        }
        field.value = target;
      } else if (field) {
        field.remove();
      }
    });
  }

  /* facets.js re-renders the mobile sort sheet but not the desktop sort
     select, so a sort chosen under «الكل» would stay displayed over a tab that
     opened in its default order. Take the value from the fetched page. */
  function syncSort(doc) {
    var value = '';
    var sel = doc.querySelector('select[name="sort_by"] option[selected]');
    var radio = doc.querySelector('input[name="sort_by"][checked]');
    if (sel) value = sel.value;
    else if (radio) value = radio.value;
    if (!value) return;
    document.querySelectorAll('select[name="sort_by"]').forEach(function (s) { s.value = value; });
    document.querySelectorAll('input[name="sort_by"]').forEach(function (r) { r.checked = r.value === value; });
  }

  /* The H1 is always the selected tab's collection title (owner, 2026-09-26);
     «الكل» carries the parent's. The header section is not re-rendered on an
     in-page switch, so the title comes from the link. The visually hidden
     prefix inside the H1 is kept; <title> keeps its store suffix. */
  function syncTitle(link) {
    var title = link.getAttribute('data-sct-title');
    if (!title) return;
    var h1 = document.querySelector('.sch__title');
    if (h1) {
      var last = h1.lastChild;
      if (last && last.nodeType === 3) last.textContent = title;
      else h1.appendChild(document.createTextNode(title));
    }
    var parts = document.title.split(' – ');
    document.title = parts.length > 1 ? title + ' – ' + parts.slice(1).join(' – ') : title;
    /* A description belongs to the collection it was rendered for: shown on
       that tab, hidden on every other one, back again on return. */
    var desc = document.querySelector('.sch__desc');
    if (desc) desc.hidden = keyOf(link.href) !== origin;
  }

  function clearPending() {
    clearTimeout(pendingTimer);
    pendingHref = '';
    rail.removeAttribute('data-sct-pending');
    status.classList.add('visually-hidden');
    var container = document.getElementById('ProductGridContainer');
    if (container) container.removeAttribute('aria-busy');
    var grid = document.getElementById('product-grid');
    if (grid) grid.removeAttribute('inert');
    var box = collectionBox();
    if (box) box.classList.remove('is-tab-pending');
  }

  function swap(href, html) {
    /* facets.js declares `class FacetFiltersForm` at the top level of a
       classic script: a global binding, but NOT a property of window. */
    var FF = typeof FacetFiltersForm !== 'undefined' ? FacetFiltersForm : null;
    if (!FF || !FF.renderProductGridContainer) throw new Error('no facets');
    var doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.getElementById('ProductGridContainer') || !doc.getElementById('ProductCount')) throw new Error('incomplete section');
    var u = new URL(href, location.href);
    var search = u.search.slice(1);
    /* URL first: sondos-collection.js reads it the moment the new grid lands. */
    history.replaceState({ searchParams: search }, '', u.pathname + u.search + u.hash);
    FF.searchParamsPrev = search;
    syncViewField(u.searchParams.get('view') || '');
    FF.renderFilters(html, null, doc);
    FF.renderProductGridContainer(html, doc);
    FF.renderProductCount(html, doc);
    syncSort(doc);
    shown = keyOf(href);
  }

  function go(link) {
    var href = link.href;
    var mine = ++ticket;
    clearPending();
    var FF = typeof FacetFiltersForm !== 'undefined' ? FacetFiltersForm : null;
    if (FF && FF.cancelRender) FF.cancelRender();
    mark(link);
    reveal(link);
    pendingHref = href;
    var container = document.getElementById('ProductGridContainer');
    if (container) container.setAttribute('aria-busy', 'true');
    var grid = document.getElementById('product-grid');
    if (grid) grid.setAttribute('inert', '');
    Object.keys(cache).forEach(function (key) {
      if (key !== keyOf(href) && cache[key].controller) {
        cache[key].controller.abort();
        delete cache[key];
      }
    });
    pendingTimer = setTimeout(function () {
      if (mine !== ticket) return;
      rail.setAttribute('data-sct-pending', '');
      status.textContent = (rail.getAttribute('data-sct-loading') || '') + ' ' + (link.getAttribute('data-sct-title') || link.textContent);
      var box = collectionBox();
      if (box) box.classList.add('is-tab-pending');
    }, PENDING_MS);

    fetchSection(href).then(function (html) {
      if (mine !== ticket) return;
      clearPending();
      try {
        swap(href, html);
        syncTitle(link);
        var count = document.getElementById('ProductCount');
        status.textContent = (link.getAttribute('data-sct-title') || link.textContent) + (count ? '، ' + count.textContent.trim() : '');
      } catch (e) {
        window.location.href = href;
      }
    }).catch(function () {
      if (mine !== ticket) return;
      clearPending();
      window.location.href = href;
    });
  }

  function onIntent(event) {
    var link = event.target.closest('a[data-sct-link]');
    if (!link || !rail.contains(link) || link.hasAttribute('aria-current')) return;
    if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
    warm(link);
  }
  rail.addEventListener('pointerover', onIntent, { passive: true });
  rail.addEventListener('focusin', onIntent);
  rail.addEventListener('pointerdown', onIntent, { passive: true });

  rail.addEventListener('click', function (event) {
    var link = event.target.closest('a[data-sct-link]');
    if (!link || !rail.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (keyOf(link.href) === shown) {
      ++ticket;
      clearPending();
      status.textContent = '';
      mark(link);
      reveal(link);
      return;
    }
    if (pendingHref === link.href) return;
    go(link);
  });

  /* A sort/filter interaction supersedes a pending tab, just as another tab
     does. Conversely cancelRender prevents an old facet response winning. */
  document.addEventListener('sondos:facets-start', function () {
    ++ticket;
    clearPending();
    status.textContent = '';
    markByLocation();
  });

  /* Back/Forward that lands on a different tab than the one on screen (e.g.
     a sort pushed an entry, then the tab changed) — load that URL as a page
     rather than trying to reconcile two in-page renderers. */
  window.addEventListener('popstate', function () {
    ++ticket;
    clearPending();
    if (keyOf(location.href) !== shown) window.location.reload();
  });

  /* bfcache return: the DOM already holds the tab she left; keep the chip in
     step with the address bar in case they ever diverge. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) markByLocation();
  });

  window.addEventListener('pagehide', function () {
    ++ticket;
    clearPending();
    markByLocation();
  });

  /* Only the unfiltered first page can seed a category tab. Captured before
     the deferred auto-load script appends further pages. */
  var initialURL = new URL(location.href);
  var clean = Array.from(initialURL.searchParams.keys()).every(function (key) {
    return ['view', 'preview_theme_id', '_fd', '_ab', 'pb'].indexOf(key) !== -1;
  });
  var initialBox = document.getElementById('ProductGridContainer');
  var initialSection = initialBox && initialBox.closest('.shopify-section');
  if (clean && initialSection) {
    cache[shown] = { time: Date.now(), promise: Promise.resolve(initialSection.outerHTML) };
  }
  if (document.readyState === 'complete') warmNeighbours();
  else window.addEventListener('load', warmNeighbours, { once: true });

  if (view) syncViewField(new URL(location.href).searchParams.get('view') || '');
})();
